import { useQuery } from '@tanstack/react-query';

import type { ProductWithThemes } from '../types';

import { buildInsightReqBody } from '@/entities/insight/utils';
import { socialApiClient } from '@/shared/api-client/social-api-client';
import type { PublicFilter } from '../../shared/public-filter/use-public-filter';

async function fetchProductsWithThemes(
  {
    queryId,
    startDate,
    endDate,
    publicFilter,
  }: {
    queryId: string;
    startDate: string;
    endDate: string;
    publicFilter: PublicFilter;
  },
  signal?: AbortSignal,
): Promise<ProductWithThemes[]> {
  const requestBody = buildInsightReqBody(startDate, endDate, 100, publicFilter);

  const response = await socialApiClient
    .post(`queries/${queryId}/insight/conversation/related/products`, {
      json: requestBody,
      signal,
    })
    .json<ProductWithThemes[]>();

  // Handle both response formats and ensure themes array exists
  return response;
}

export function useProductsWithThemes(params: Parameters<typeof fetchProductsWithThemes>[0]) {
  const query = useQuery({
    queryKey: [
      'products-with-themes',
      params.queryId,
      params.startDate,
      params.endDate,
      JSON.stringify(params.publicFilter),
    ],
    queryFn: ({ signal }) => fetchProductsWithThemes(params, signal),
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });

  return query;
}
